import Calendar from '@/components/calendar/Calendar';
import { Button } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { Textarea } from '@/components/common/Textarea';
import UsersCombobox from '@/components/general/UsersCombobox';
import { PASSWORD_VALIDATE_REGEX, PHONE_VALIDATE_REGEX, ROUTES, SHOP_OWNER } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { LANGUAGES_SUPPORTED, SHOP_OWNER_TYPE, USER_STATUS_TYPE } from '@/types/enums';
import { isExpired } from '@/utils/datetime';
import { toastify } from '@/utils/toastify';
import { generateDefaultPassword } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface FormReferralCodeProps {
    reLoadData: Function,
    modeForm: 'create' | 'edit',
    id?: string | number
}

export default observer(function FormReferralCodeModal({ reLoadData, modeForm, id }: FormReferralCodeProps) {
    // hooks
    const { t, i18n: { language } } = useTranslation();
    const navigate = useNavigate();

    // validate Schema
    const validateSchema = yup.object().shape({
        referralCode: yup.string().required('form_error_validate.required'),
        expiredDate: yup.date().nullable(),
        organizationName: yup.string().required('form_error_validate.required'),
        status: yup.boolean().required('form_error_validate.required'),
        note: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        referralCode: '',
        expiredDate: null,
        organizationName: '',
        note: '',
        status: true
    };

    const form = useForm({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: initialValues
    });

    const watchExpiredDate = form.watch('expiredDate');

    // store
    const {
        referralCodeStore: { createReferralCode, getReferralCodeInfo, updateReferralCode },
        modalStore: { hideModal, showAlertModal, modalWaringAlertOptions }
    } = useStore();

    // lifecycle
    useEffect(() => {
        fetchData();
    }, [id]);

    // function
    const fetchData = async () => {
        if (modeForm === 'edit' && id) {
            const res = await flowResult(getReferralCodeInfo(id));
            if (res) {
                const { code, expiredDate, organizationName, status, note } = res;
                form.reset({
                    referralCode: code ?? '',
                    expiredDate: expiredDate ? new Date(expiredDate) : null,
                    organizationName: organizationName ?? '',
                    status: status ?? true,
                    note: note ?? ''
                });
            }
        }
    };

    const onSubmit = async (data: FormData) => {
        const { referralCode, expiredDate, organizationName, note, status } = data;

        switch (modeForm) {
            case 'create':
                const createCode = async () => {
                    const res = await flowResult(createReferralCode({
                        referralCode: referralCode ?? '',
                        expiredDate: expiredDate ?? null,
                        organizationName: organizationName ?? '',
                        note: note ?? ''
                    }));
                    if (res) {
                        toastify('success', t('messages.add_success'));
                        reLoadData();
                        hideModal();
                    }
                };

                if (expiredDate && moment(expiredDate).isBefore(moment(), 'day')) {
                    showAlertModal(modalWaringAlertOptions({
                        hideIconCenter: true,
                        content: t('messages.confirm_add_referral_code_expired'),
                        onSave: createCode
                    }));
                } else {
                    await createCode();
                }

                break;
            case 'edit':
                if (id) {
                    const resEdit = await flowResult(updateReferralCode(id, {
                        expiredDate: expiredDate ?? null,
                        organizationName: organizationName ?? '',
                        note: note ?? '',
                        status: status
                    }));

                    if (resEdit) {
                        toastify('success', t('messages.update_success'));
                        reLoadData();
                        hideModal();
                    }
                }
                break;
            default:
                break;
        }
    };

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className='grid grid-cols-2 gap-y-4 mb-4'>
                        <FormField
                            control={form.control}
                            name='referralCode'
                            render={({ field }) => (
                                <FormItem>
                                    <div className='flex items-center gap-4'>
                                        <Label className='label-1 w-2/6 whitespace-nowrap md:min-w-[142px] ' required>{t('words_title.referral_modal_referral_code')}</Label>
                                        {modeForm === 'create' ?
                                            (
                                                <>
                                                    <FormControl className='w-4/6'>
                                                        <Input type='text' {...field} maxLength={50} />
                                                    </FormControl>
                                                </>
                                            ) :
                                            (
                                                <>
                                                    <div className='h-[48px] flex flex-col justify-center'>
                                                        <span style={{ wordBreak: 'break-word' }}>{field.value}</span>
                                                    </div>
                                                </>
                                            )}
                                    </div>
                                    <div className='flex items-center gap-4'>
                                        <div className='w-2/6'></div>
                                        <FormMessage className='w-4/6' />
                                    </div>
                                </FormItem>
                            )}
                        />

                        <div className='pl-4'>
                            <FormField
                                control={form.control}
                                name='expiredDate'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center gap-4'>
                                            <Label className='label-1 w-2/6'>{t('words_title.referral_modal_expired_date')}</Label>
                                            <FormControl className='w-4/6'>
                                                <Calendar
                                                    mode='single'
                                                    selected={field.value}
                                                    onSelect={field.onChange}
                                                    useIconRemove={true}
                                                    className='w-full'
                                                    required={false}
                                                    placeholder=''
                                                />
                                            </FormControl>
                                        </div>
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    <div className='grid grid-cols-1 gap-x-20 gap-y-4 mb-4'>
                        <FormField
                            control={form.control}
                            name='organizationName'
                            render={({ field }) => (
                                <FormItem>
                                    <div className='flex items-center gap-4'>
                                        <Label className='label-1 w-1/6' required>{t('words_title.referral_modal_organization_name')}</Label>
                                        <FormControl className='w-5/6'>
                                            <Input type='text' {...field} maxLength={255} />
                                        </FormControl>
                                    </div>
                                    <div className='flex items-center gap-4'>
                                        <div className='w-1/6'></div>
                                        <FormMessage className='w-5/6' />
                                    </div>
                                </FormItem>
                            )}
                        />
                    </div>

                    <FormField
                        control={form.control}
                        name='note'
                        render={({ field }) => (
                            <FormItem>
                                <div className='flex items-center gap-4'>
                                    <Label className='label-1 w-1/6'>{t('words_title.referral_modal_note')}</Label>
                                    <FormControl className='w-5/6'>
                                        <Textarea
                                            className='mt-[0.375rem] resize-none h-[133px]'
                                            value={field.value ?? ''}
                                            maxLength={5000}
                                            onChange={e => field.onChange(e.target.value)}
                                        />
                                    </FormControl>
                                </div>
                            </FormItem>
                        )}
                    />

                    {
                        !isExpired(watchExpiredDate) && modeForm === 'edit' && (
                            <FormField
                                control={form.control}
                                name='status'
                                render={({ field }) => (
                                    <FormItem>
                                        <div className='flex items-center gap-4 pb-4 mt-4'>
                                            <Label className='label-1 w-1/6' required>{t('words_title.status')}</Label>
                                            <FormControl className='w-5/6'>
                                                <RadioGroup
                                                    className='flex items-center'
                                                    onValueChange={(value) => {
                                                        field.onChange(value);
                                                    }}
                                                    value={field.value.toString()}
                                                >
                                                    <FormItem className='flex w-44 items-center gap-2'>
                                                        <FormControl>
                                                            <RadioGroupItem id='trueRadioStatus' value='true' />
                                                        </FormControl>
                                                        <Label
                                                            className='!my-0 cursor-pointer'
                                                            htmlFor='trueRadioStatus'
                                                        >
                                                            {t('words_title.shop_status_active')}
                                                        </Label>
                                                    </FormItem>
                                                    <FormItem className='flex w-44 items-center gap-2'>
                                                        <FormControl>
                                                            <RadioGroupItem id='falseRadioStatus' value='false' />
                                                        </FormControl>
                                                        <Label
                                                            className='!my-0 cursor-pointer'
                                                            htmlFor='falseRadioStatus'
                                                        >
                                                            {t('words_title.user_status_inactive_promotion')}
                                                        </Label>
                                                    </FormItem>
                                                </RadioGroup>
                                            </FormControl>
                                        </div>
                                        <div className='flex items-center gap-4'>
                                            <div className='w-1/6'></div>
                                            <FormMessage className='w-5/6' />
                                        </div>
                                    </FormItem>
                                )}
                            />
                        )
                    }

                    <div className='my-8 flex gap-8 items-center justify-center'>
                        <Button
                            onClick={() => hideModal()}
                            type='button'
                            className='min-w-48'
                            variant='outline'
                        >
                            {t('button.cancel')}
                        </Button>
                        <Button type='submit' className='min-w-48'>{modeForm === 'edit' ? t('button.save') : t('button.add')}</Button>
                    </div>
                </form>
            </Form>
        </div>
    );
});
